import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapPin, Calendar, ChevronRight, Star, Building2, Clock, ExternalLink, ArrowRight, Sparkles } from 'lucide-react';
import Layout from '../../components/layout/layout';
import StaticMetaTags from '../../components/seo/StaticMetaTags';
import JobPostingSchema from '../../components/seo/JobPostingSchema';
import { ContractBadge, RemoteBadge } from '../../components/offres/OfferBadge';
import CandidatureForm from '../../components/offres/CandidatureForm';
import { getPublishedJobOfferBySlug } from '../../lib/offres';
import { supabase } from '../../lib/supabase';
import type { JobOffer } from '../../types/offres';

type LinkedParcours = { slug: string; company_name: string | null };

export function OffreDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [offer, setOffer] = useState<JobOffer | null>(null);
  const [linkedParcours, setLinkedParcours] = useState<LinkedParcours | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) {
      navigate('/offres');
      return;
    }

    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        const data = await getPublishedJobOfferBySlug(slug!);
        if (!mounted) return;
        if (!data) {
          setNotFound(true);
        } else {
          setOffer(data);
          if (data.parcours_id) {
            const { data: p } = await supabase
              .from('parcours')
              .select('slug, company_name')
              .eq('id', data.parcours_id)
              .maybeSingle();
            if (mounted && p) setLinkedParcours(p as LinkedParcours);
          }
        }
      } catch {
        if (mounted) setNotFound(true);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, [slug, navigate]);

  const canonicalUrl = `https://recruteurs.io/offres/${slug}/`;

  const publishedDate = offer?.published_at
    ? new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(offer.published_at))
    : null;

  const validThroughDate = offer?.valid_through
    ? new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(offer.valid_through))
    : null;

  if (loading) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-pink-600 border-t-transparent" />
        </div>
      </Layout>
    );
  }

  if (notFound || !offer) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl px-6 py-32 text-center">
          <h1 className="text-2xl font-bold text-slate-900">Offre introuvable</h1>
          <p className="mt-3 text-slate-600">Cette offre n'existe pas ou n'est plus disponible.</p>
          <Link to="/offres" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-pink-600 px-5 py-2.5 text-sm font-semibold whitespace-nowrap text-white hover:bg-pink-700 transition-colors">
            Voir toutes les offres
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <StaticMetaTags
        title={offer.meta_title ?? `${offer.title} — recruteurs.io`}
        description={offer.meta_description ?? `${offer.title} · ${offer.location_city} · Postulez sur recruteurs.io`}
        url={canonicalUrl}
      />
      <JobPostingSchema offer={offer} url={canonicalUrl} />

      <main className="min-h-screen bg-slate-50">
        {/* Breadcrumb */}
        <div className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-5xl px-6 py-3">
            <nav className="flex items-center gap-1.5 text-sm text-slate-500">
              <Link to="/" className="hover:text-slate-700 transition-colors">Accueil</Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <Link to="/offres" className="hover:text-slate-700 transition-colors">Offres d'emploi</Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="text-slate-900 font-medium truncate max-w-xs">{offer.title}</span>
            </nav>
          </div>
        </div>

        <div className="mx-auto max-w-5xl px-6 py-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_320px]">

            {/* Main content */}
            <div className="space-y-6">

              {/* Header card */}
              <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                <div className="flex items-start gap-5">
                  {offer.company_logo_url ? (
                    <img
                      src={offer.company_logo_url}
                      alt={offer.company_name ?? offer.title}
                      className="h-16 w-16 rounded-xl border border-slate-100 object-contain p-1.5 shrink-0"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border border-slate-100 bg-slate-50 text-2xl font-bold text-slate-400">
                      {offer.title.charAt(0).toUpperCase()}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    {offer.featured && (
                      <div className="mb-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                        <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                        Mise en avant
                      </div>
                    )}
                    <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{offer.title}</h1>
                    <p className="mt-1 text-base font-medium text-slate-600">
                      {offer.company_name ?? offer.hiring_organization_name ?? 'recruteurs.io'}
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <ContractBadge type={offer.contract_type} />
                  <RemoteBadge type={offer.remote_type} />
                  {offer.sector && (
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                      {offer.sector}
                    </span>
                  )}
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
                  <span className="flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    {offer.location_city}
                    {offer.job_location_postal_code && ` (${offer.job_location_postal_code})`}
                  </span>
                  {publishedDate && (
                    <span className="flex items-center gap-1.5">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      Publiée le {publishedDate}
                    </span>
                  )}
                  {validThroughDate && (
                    <span className="flex items-center gap-1.5">
                      <Clock className="h-4 w-4 text-slate-400" />
                      Jusqu'au {validThroughDate}
                    </span>
                  )}
                </div>

                {offer.salary_range && (
                  <div className="mt-4 rounded-xl bg-slate-50 px-4 py-2.5">
                    <span className="text-sm font-semibold text-slate-800">{offer.salary_range}</span>
                  </div>
                )}

              </div>

              {/* Description */}
              <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                <h2 className="text-lg font-bold text-slate-900">Description du poste</h2>
                <div className="mt-4 prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-line">
                  {offer.description}
                </div>
              </div>

              {/* Profile */}
              <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
                <h2 className="text-lg font-bold text-slate-900">Profil recherché</h2>
                <div className="mt-4 prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-line">
                  {offer.profile}
                </div>
              </div>

            </div>

            {/* Sidebar */}
            <div className="space-y-5">

              {/* Details card */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-base font-bold text-slate-900 mb-4">Détails</h3>
                <dl className="space-y-3 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-slate-500">Contrat</dt>
                    <dd><ContractBadge type={offer.contract_type} /></dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-slate-500">Télétravail</dt>
                    <dd><RemoteBadge type={offer.remote_type} /></dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-slate-500">Localisation</dt>
                    <dd className="font-medium text-slate-700 text-right">{offer.location_city}</dd>
                  </div>
                  {offer.sector && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">Secteur</dt>
                      <dd className="font-medium text-slate-700 text-right">{offer.sector}</dd>
                    </div>
                  )}
                  {offer.salary_range && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">Rémunération</dt>
                      <dd className="font-medium text-slate-700 text-right">{offer.salary_range}</dd>
                    </div>
                  )}
                  {offer.internal_reference && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">Référence</dt>
                      <dd className="font-mono text-xs text-slate-500">{offer.internal_reference}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Company card */}
              {(offer.company_name || offer.hiring_organization_name) && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="text-base font-bold text-slate-900 mb-3">À propos</h3>
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-slate-400 shrink-0" />
                    <span className="text-sm font-medium text-slate-700">
                      {offer.hiring_organization_name ?? offer.company_name}
                    </span>
                  </div>
                  {offer.hiring_organization_url && (
                    <a
                      href={offer.hiring_organization_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 flex items-center gap-1.5 text-sm text-pink-600 hover:text-pink-700 transition-colors"
                    >
                      Visiter le site
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Parcours discovery CTA */}
          {linkedParcours && (
            <div className="mt-10 rounded-2xl border border-[#d03484]/20 bg-gradient-to-r from-[#d03484]/5 to-[#d47254]/5 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">
                    Envie d'en savoir plus sur {linkedParcours.company_name ?? 'l\'entreprise'} avant de postuler ?
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Culture, équipe, valeurs — découvrez tout avant de candidater.
                  </p>
                </div>
              </div>
              <Link
                to={`/decouvrir/${linkedParcours.slug}`}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all shrink-0"
              >
                Découvrir l'entreprise
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {/* Candidature form */}
          <div className="mt-10">
            <CandidatureForm
              missionTitle={offer.title}
              offerId={offer.id}
              offerSlug={offer.slug}
            />
          </div>
        </div>
      </main>
    </Layout>
  );
}
